import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, RefreshCw, Compass, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Career() {
  const { t, lang } = useLang();
  const [phase, setPhase] = useState("intro"); // intro | quiz | result
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (phase === "quiz" && questions.length === 0) {
      axios
        .get(`${BACKEND_URL}/api/quiz/questions?language=${lang}`)
        .then((r) => setQuestions(r.data))
        .catch(() => setQuestions([]));
    }
  }, [phase, lang, questions.length]);

  useEffect(() => {
    // Reload questions when language changes mid-quiz? Only if not started answering
    if (phase === "quiz" && answers.length === 0 && questions.length > 0) {
      axios
        .get(`${BACKEND_URL}/api/quiz/questions?language=${lang}`)
        .then((r) => setQuestions(r.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const startQuiz = () => {
    setPhase("quiz");
    setIdx(0);
    setAnswers([]);
    setResult(null);
  };

  const selectOption = async (trait) => {
    const next = [...answers, trait];
    setAnswers(next);
    if (idx + 1 < questions.length) {
      setIdx(idx + 1);
    } else {
      // submit
      setSubmitting(true);
      try {
        const res = await axios.post(`${BACKEND_URL}/api/quiz/submit`, {
          answers: next,
          language: lang,
        });
        setResult(res.data);
        setPhase("result");
      } catch (e) {
        setPhase("result");
      } finally {
        setSubmitting(false);
      }
    }
  };

  const retake = () => {
    setPhase("intro");
    setAnswers([]);
    setResult(null);
    setQuestions([]);
    setIdx(0);
  };

  if (phase === "intro") {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16 text-center fade-up">
        <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto rounded-3xl bg-secondary/40 flex items-center justify-center mb-6 sm:mb-8">
          <Compass className="w-10 h-10 sm:w-12 sm:h-12 text-primary" strokeWidth={2.5} />
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground">{t("career_hub")}</h1>
        <p className="mt-4 sm:mt-5 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">{t("career_desc")}</p>
        <Button
          data-testid="start-quiz-btn"
          onClick={startQuiz}
          className="pill-btn pill-btn-primary mt-8 sm:mt-10 gap-2 h-auto text-base sm:text-lg"
        >
          {t("start_quiz")} <ArrowRight className="w-5 h-5" />
        </Button>
      </div>
    );
  }

  if (phase === "quiz") {
    if (questions.length === 0) {
      return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-muted-foreground">Loading...</div>;
    }
    const q = questions[idx];
    const progress = ((idx) / questions.length) * 100;

    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-5 sm:mb-6">
          <div className="flex items-center justify-between text-sm font-semibold text-muted-foreground mb-3">
            <span data-testid="question-counter">
              {t("question_of")} {idx + 1} {t("of")} {questions.length}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <div key={q.id} className="fade-up">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground leading-snug mb-6 sm:mb-8">
            {q.text}
          </h2>
          <div className="grid gap-3 sm:gap-4">
            {q.options.map((opt, i) => (
              <button
                key={i}
                data-testid={`quiz-option-${idx}-${i}`}
                onClick={() => selectOption(opt.trait)}
                disabled={submitting}
                className="text-left w-full bg-white border-2 border-border rounded-2xl p-4 sm:p-5 hover:border-primary hover:bg-primary/5 transition-colors active:scale-[0.99] disabled:opacity-50"
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-secondary/40 flex items-center justify-center text-primary font-bold flex-shrink-0">
                    {String.fromCharCode(65 + i)}
                  </div>
                  <span className="text-base sm:text-lg text-foreground font-medium">{opt.text}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Result
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 fade-up">
      <div className="bg-white border border-border rounded-3xl p-5 sm:p-8 shadow-[0_16px_48px_rgba(30,63,45,0.08)]">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-primary text-white flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="text-xs sm:text-sm uppercase tracking-widest text-muted-foreground font-semibold">
              {t("your_personality")}
            </div>
            <div data-testid="personality-result" className="text-2xl sm:text-3xl font-bold text-foreground truncate">
              {result?.personality || "—"}
            </div>
          </div>
        </div>

        <h3 className="mt-6 sm:mt-8 text-lg sm:text-xl font-bold text-foreground">{t("suggested_careers")}</h3>
        <div className="mt-4 grid gap-3" data-testid="career-list">
          {(result?.careers || []).map((c, i) => (
            <div key={i} className="bg-[#F7F4EB] border border-border rounded-2xl p-4">
              <div className="font-bold text-foreground text-lg">{c.name}</div>
              <div className="text-sm text-muted-foreground mt-1">{c.why}</div>
            </div>
          ))}
        </div>

        <Button
          data-testid="retake-btn"
          onClick={retake}
          className="pill-btn pill-btn-secondary mt-8 gap-2 h-auto"
        >
          <RefreshCw className="w-5 h-5" /> {t("retake")}
        </Button>
      </div>
    </div>
  );
}
